import { lastRouteKey, lastWorkspaceKey, validateRedirect } from "@baton/core";
import { ErrorState } from "@baton/ui";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation, useParams } from "react-router";
import { useRuntime } from "../app/RuntimeProvider.tsx";
import { problemOf } from "../app/problems.ts";
import { cyclesQuery, meQuery } from "../app/queries.ts";
import { openPortalsByTeam } from "../portal/standing.ts";
import { CycleDetailSkeleton, ShellSkeleton } from "../skeletons/Skeletons.tsx";
import { OutsideShell } from "./OutsideShell.tsx";

/**
 * FR-015-006, `contracts/screens.md` § Route map: `/` goes to the last workspace, else to
 * the first membership, else, for a client member of 028 holding a live portal, to that Team's
 * Tickets (2026-10-21), else to `/welcome`, which asks a person with no membership whether to
 * join a Team that invited them or create one (FR-015-172, 2026-09-28); `/{ws}` goes to `/{ws}/dashboard`, Home's front page since 2026-09-28 (FR-015-162). The `/me` read was seeded by the bootstrap
 * probe of `main.tsx`, which is where nobody-signed-in leaves for `/sign-in`.
 */
export function RootRedirect() {
  const runtime = useRuntime();
  const me = useQuery(meQuery(runtime));
  if (me.isPending) return <ShellSkeleton />;
  if (me.isError) {
    return (
      <OutsideShell>
        <ErrorState form="full-page" problem={problemOf(me.error)} onRetry={() => void me.refetch()} />
      </OutsideShell>
    );
  }
  // A Team an instance admin suspended (001 FR-159) opens on its suspended page, so `/` goes to an open Team when the person has one, and to the suspended one only when it is all they have.
  const memberships = [...me.data.memberships.filter((m) => m.suspended_at === null), ...me.data.memberships.filter((m) => m.suspended_at !== null)];
  const last = runtime.storage.get(lastWorkspaceKey);
  const remembered = last !== null ? memberships.find((m) => m.slug === last && m.suspended_at === null) : undefined;
  const target = remembered ?? memberships[0];
  // The frame stays on screen through the navigation's commit, so `/` never paints an empty document.
  if (target === undefined) {
    // A client member, in no Team and holding a live portal (028 FR-028-046), lands on their tickets rather than on the welcome screen.
    const portal = openPortalsByTeam(me.data)[0]?.portals[0];
    return (
      <>
        <ShellSkeleton />
        <Navigate to={portal === undefined ? "/welcome" : `/${portal.workspace.slug}/tickets`} replace />
      </>
    );
  }
  const route = validateRedirect(runtime.storage.get(lastRouteKey, target.slug));
  return (
    <>
      <ShellSkeleton />
      <Navigate to={route?.startsWith(`/${target.slug}/`) === true ? route : `/${target.slug}/dashboard`} replace />
    </>
  );
}

/** `/{ws}/projects/{project}` opens the project's item list, keeping the query (2026-09-15, by the owner's decision, after Plane; `contracts/screens.md` § Route map). A remembered route that still names the bare project lands on the list too. */
export function ProjectRedirect() {
  const { ws = "", project = "" } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${ws}/projects/${encodeURIComponent(project)}/tasks${search}`} replace />;
}

/** A client's insights page by its address before 2026-10-02, when its two lists of notes became one: the client's `/insights`, with the query and the hash kept, for a bookmark of that page. */
export function ClientInsightsRedirect() {
  const { ws = "", account = "" } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${ws}/clients/${encodeURIComponent(account)}/insights${search}${hash}`} replace />;
}

/** `/{ws}/settings` and `/{ws}/projects/{project}/settings` open their General page (2026-09-17), so a shortened link never lands on Page not found. */
export function WorkspaceSettingsRedirect() {
  const { ws = "" } = useParams();
  return <Navigate to={`/${ws}/settings/general`} replace />;
}

export function ProjectSettingsRedirect() {
  const { ws = "", project = "" } = useParams();
  return <Navigate to={`/${ws}/projects/${encodeURIComponent(project)}/settings/general`} replace />;
}

export function WorkspaceIndexRedirect() {
  const { ws = "" } = useParams();
  return <Navigate to={`/${ws}/dashboard`} replace />;
}

/**
 * `/{ws}/sprints/active`, the sidebar's Sprints row (2026-09-17, by the owner's decision, after the
 * meeting of that day): the active sprint's page in one click, as Inbox and My work are one click,
 * with the sprints list a link from that page; when no sprint is active the list itself. A read
 * that fails falls through to the list, which shows the error.
 */
export function CycleActiveRedirect() {
  const runtime = useRuntime();
  const { ws = "" } = useParams();
  const cycles = useQuery(cyclesQuery(runtime, ws));
  if (cycles.isPending) return <CycleDetailSkeleton />;
  const active = cycles.data?.find((cycle) => cycle.status === "active");
  return <Navigate to={active === undefined ? `/${ws}/sprints` : `/${ws}/sprints/${active.id}`} replace />;
}

/**
 * The address bar says what the screen says (2026-09-18, by the owner's decision): `/sprints`, `/tasks`
 * and `/all-tasks` where the code, the API and the specs say cycle and item, and `/clients` and
 * `/client-groups` where they say account (2026-09-29, by the owner's review: "shouldnt it be clients
 * in the url.."). Every link that carried
 * the old form, a bookmark, a shared link, the palette's remembered routes, lands here and moves to the
 * new one with its query and hash kept.
 */
const LEGACY_SEGMENTS: readonly (readonly [RegExp, string])[] = [
  [/^\/([^/]+)\/cycles(?=\/|$)/, "/$1/sprints"],
  [/^\/([^/]+)\/items(?=\/|$)/, "/$1/tasks"],
  [/^\/([^/]+)\/all-items(?=\/|$)/, "/$1/all-tasks"],
  [/^\/([^/]+)\/projects\/([^/]+)\/items(?=\/|$)/, "/$1/projects/$2/tasks"],
  [/^\/([^/]+)\/accounts(?=\/|$)/, "/$1/clients"],
  [/^\/([^/]+)\/account-groups(?=\/|$)/, "/$1/client-groups"],
  // The backlog under the sprints, its address on 2026-10-02 until the owner's decision that day that it is no part of them.
  [/^\/([^/]+)\/sprints\/backlog$/, "/$1/backlog"],
  // Client items, renamed Client tasks on 2026-10-13 by the owner's decision, the address with it.
  [/^\/([^/]+)\/client-items$/, "/$1/client-tasks"],
];

/** The current spelling of an old path, every rule applied in order, so `/cycles/backlog` lands on `/backlog` in one move; null when no rule applies. */
export function legacyPathOf(pathname: string): string | null {
  const target = LEGACY_SEGMENTS.reduce((path, [pattern, replacement]) => path.replace(pattern, replacement), pathname);
  return target === pathname ? null : target;
}

export function LegacyRedirect() {
  const { pathname, search, hash } = useLocation();
  const target = legacyPathOf(pathname);
  return <Navigate to={target === null ? pathname : `${target}${search}${hash}`} replace />;
}
